import { useState, type FunctionComponent, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Collapse, InputNumber, Select, Space, Typography, message } from "antd";

import {
  ChallengeGoalType,
  ChallengeLeaderboardVisibility,
  DEFAULT_CHALLENGE_COVER_POSITION,
  type ChallengeAudienceSpec,
  type ChallengeEntity,
} from "@fitness-app/data-models/entities/Challenge";
import { ChallengeFeedMode } from "@fitness-app/data-models/entities/ChallengeFeed";

import { supabase } from "~/store/initializeStore";
import ChallengeAudienceModal from "./ChallengeAudienceModal";
import ChallengeCoverField from "./ChallengeCoverField";
import { challengeGoalTypeOptions, generateJoinCode, leaderboardVisibilityOptions } from "./ChallengeFormDrawer";
import { challengeFeedModeOptions } from "./constants";

/**
 * One setting per row: label + description on the left, control on the right. A multi-column grid
 * left ragged holes wherever a control was short, and reading it meant zig-zagging across the page.
 */
const Field: FunctionComponent<{ label: string; description?: string; children: ReactNode }> = ({
  label,
  description,
  children,
}) => (
  <div className="flex flex-col gap-2 border-b border-[#f5f5f5] py-4 last:border-b-0 md:flex-row md:items-start md:gap-8">
    <div className="md:w-[260px] md:shrink-0">
      <Typography.Text strong>{label}</Typography.Text>
      {description ? (
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {description}
          </Typography.Text>
        </div>
      ) : null}
    </div>
    <div className="min-w-0 flex-1 md:max-w-[420px]">{children}</div>
  </div>
);

/** Every setting here is a single-column patch on `challenge` — one hook covers all of them. */
const usePatchChallenge = (challengeId: string, successMessage: string, onSaved?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const { error } = await supabase
        .from("challenge")
        .update(patch as never)
        .eq("id", challengeId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void message.success(successMessage);
      void queryClient.invalidateQueries({ queryKey: ["challenge-detail", challengeId] });
      onSaved?.();
    },
    onError: (error: Error) => void message.error(`Nie udało się zapisać: ${error.message}`),
  });
};

type Props = {
  challengeId: string;
  challenge: ChallengeEntity | undefined;
};

/**
 * Everything editable about a challenge, in a grid, behind a collapse. The identity block (name,
 * dates, cover) stays in the header; the knobs a trainer touches once live here instead of pushing
 * the tabs off the first screen.
 */
const ChallengeSettingsPanel: FunctionComponent<Props> = ({ challengeId, challenge }) => {
  const queryClient = useQueryClient();
  const [audienceModalOpen, setAudienceModalOpen] = useState(false);
  // TRE-1577: switching to threshold/both without a stored pass mark is parked here until the
  // trainer enters the points, then BOTH fields are saved atomically (the DB constraint
  // challenge_goal_pass_points_check rejects a threshold challenge without a pass mark).
  const [pendingGoalType, setPendingGoalType] = useState<ChallengeGoalType | null>(null);

  const updateJoinCode = usePatchChallenge(challengeId, "Ustawienia dołączania zaktualizowane");
  const updateVisibility = usePatchChallenge(challengeId, "Widoczność rankingu zaktualizowana");
  const updateCover = usePatchChallenge(challengeId, "Okładka zaktualizowana");
  const updateCoverPosition = usePatchChallenge(challengeId, "Kadr okładki zaktualizowany");
  const updateFeedMode = usePatchChallenge(challengeId, "Tryb tablicy zaktualizowany", () => {
    void queryClient.invalidateQueries({ queryKey: ["challenge-feed", challengeId] });
  });
  const updateGoal = usePatchChallenge(challengeId, "Cel wyzwania zaktualizowany", () => {
    setPendingGoalType(null);
    void queryClient.invalidateQueries({ queryKey: ["challenge-leaderboard", challengeId] });
  });

  const goalType =
    pendingGoalType ?? (challenge?.goalType as ChallengeGoalType | undefined) ?? ChallengeGoalType.Leaderboard;
  const needsPassPoints = goalType !== ChallengeGoalType.Leaderboard;

  return (
    <>
      <Collapse
        ghost
        items={[
          {
            key: "settings",
            label: <Typography.Text strong>Ustawienia wyzwania</Typography.Text>,
            children: (
              <div className="flex flex-col">
                <Field label="Dołączanie" description="Czy klientki mogą dołączyć samodzielnie i czy potrzebują kodu">
                  {challenge?.selfJoinEnabled ? (
                    challenge.joinCode ? (
                      <Space size={4} wrap>
                        <Typography.Text copyable code>
                          {challenge.joinCode}
                        </Typography.Text>
                        <Button
                          size="small"
                          type="link"
                          loading={updateJoinCode.isLoading}
                          onClick={() => updateJoinCode.mutate({ joinCode: null })}
                        >
                          Usuń kod (otwarte)
                        </Button>
                      </Space>
                    ) : (
                      <Space size={4} wrap>
                        <Typography.Text>Otwarte — bez kodu</Typography.Text>
                        <Button
                          size="small"
                          type="link"
                          loading={updateJoinCode.isLoading}
                          onClick={() => updateJoinCode.mutate({ joinCode: generateJoinCode() })}
                        >
                          Wygeneruj kod
                        </Button>
                      </Space>
                    )
                  ) : (
                    <Typography.Text type="secondary">Tylko przypisanie przez trenera</Typography.Text>
                  )}
                </Field>

                <Field label="Grupa docelowa" description="Kto może zobaczyć i dołączyć do otwartego wyzwania">
                  <Button block onClick={() => setAudienceModalOpen(true)}>
                    {challenge?.audience ? "Wybrany segment — zmień" : "Cała organizacja — zmień"}
                  </Button>
                </Field>

                <Field label="Widoczność rankingu" description="Ile z rankingu widzi uczestniczka">
                  <Select
                    style={{ width: "100%" }}
                    value={
                      (challenge?.leaderboardVisibility as ChallengeLeaderboardVisibility | undefined) ??
                      ChallengeLeaderboardVisibility.Full
                    }
                    options={leaderboardVisibilityOptions}
                    loading={updateVisibility.isLoading}
                    onChange={(leaderboardVisibility) => updateVisibility.mutate({ leaderboardVisibility })}
                  />
                </Field>

                <Field label="Cel wyzwania" description="Rywalizacja o miejsca czy próg punktowy do zaliczenia">
                  <Space.Compact style={{ width: "100%" }}>
                    <Select
                      style={{ flex: 1 }}
                      value={goalType}
                      options={challengeGoalTypeOptions}
                      loading={updateGoal.isLoading}
                      onChange={(nextGoalType) => {
                        if (nextGoalType === ChallengeGoalType.Leaderboard) {
                          setPendingGoalType(null);
                          updateGoal.mutate({ goalType: nextGoalType, passPoints: null });
                        } else if (challenge?.passPoints) {
                          updateGoal.mutate({ goalType: nextGoalType });
                        } else {
                          setPendingGoalType(nextGoalType);
                        }
                      }}
                    />
                    {needsPassPoints ? (
                      <InputNumber
                        min={1}
                        style={{ width: 120 }}
                        placeholder="próg"
                        status={pendingGoalType ? "warning" : undefined}
                        defaultValue={challenge?.passPoints ?? undefined}
                        onBlur={(event) => {
                          const value = Number(event.target.value);
                          if (!Number.isFinite(value) || value <= 0) {
                            return;
                          }
                          if (pendingGoalType) {
                            updateGoal.mutate({ goalType: pendingGoalType, passPoints: value });
                          } else if (value !== challenge?.passPoints) {
                            updateGoal.mutate({ passPoints: value });
                          }
                        }}
                        addonAfter="pkt"
                      />
                    ) : null}
                  </Space.Compact>
                  {pendingGoalType ? (
                    <Typography.Text type="warning" style={{ fontSize: 12 }}>
                      Podaj próg punktowy, aby zapisać
                    </Typography.Text>
                  ) : null}
                </Field>

                <Field label="Tablica" description="Kto może publikować i komentować na tablicy wyzwania">
                  <Select
                    style={{ width: "100%" }}
                    value={(challenge?.feedMode as ChallengeFeedMode | undefined) ?? ChallengeFeedMode.None}
                    options={challengeFeedModeOptions}
                    loading={updateFeedMode.isLoading}
                    onChange={(feedMode) => updateFeedMode.mutate({ feedMode })}
                  />
                </Field>

                <Field label="Okładka" description="Widoczna na karcie wyzwania i w nagłówku w aplikacji klientki">
                  <ChallengeCoverField
                    challengeId={challengeId}
                    challengeContext={{
                      name: challenge?.name ?? "",
                      description: challenge?.description ?? null,
                      startDate: challenge?.startDate ?? null,
                      endDate: challenge?.endDate ?? null,
                    }}
                    value={challenge?.coverImage ?? null}
                    onChange={(coverImage) => updateCover.mutate({ coverImage })}
                    positionX={challenge?.coverPositionX ?? DEFAULT_CHALLENGE_COVER_POSITION}
                    positionY={challenge?.coverPositionY ?? DEFAULT_CHALLENGE_COVER_POSITION}
                    onPositionChange={(coverPositionX, coverPositionY) =>
                      updateCoverPosition.mutate({ coverPositionX, coverPositionY })
                    }
                  />
                </Field>
              </div>
            ),
          },
        ]}
      />

      <ChallengeAudienceModal
        open={audienceModalOpen}
        challengeId={challengeId}
        audience={(challenge?.audience as ChallengeAudienceSpec | null) ?? null}
        onClose={() => setAudienceModalOpen(false)}
      />
    </>
  );
};

export default ChallengeSettingsPanel;
